import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Warning,
  TrendingUp,
  TrendingDown,
  Schedule,
  ErrorOutline,
  AttachMoney,
  PeopleOutline,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { cashFlowService } from '../services/cashFlowService';
import {
  CashFlowForecastResponse,
  ClientForecastRow,
} from '../types/cashFlow';

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const getRiskColor = (risk: string | null): string => {
  switch (risk) {
    case 'LOW':
      return '#4caf50';
    case 'MEDIUM':
      return '#ff9800';
    case 'HIGH':
      return '#f44336';
    case 'CRITICAL':
      return '#9c27b0';
    default:
      return '#9e9e9e';
  }
};

const getRiskLabel = (risk: string | null): string => {
  switch (risk) {
    case 'LOW':
      return 'Bajo';
    case 'MEDIUM':
      return 'Medio';
    case 'HIGH':
      return 'Alto';
    case 'CRITICAL':
      return 'Critico';
    default:
      return 'N/A';
  }
};

interface KPICardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, subtitle, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box sx={{ color, mr: 1 }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const CashFlow: React.FC = () => {
  const [forecast, setForecast] = useState<CashFlowForecastResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [horizonWeeks, setHorizonWeeks] = useState(12);

  const loadForecast = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cashFlowService.getForecast(horizonWeeks);
      setForecast(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cargar el pronostico de flujo de caja');
    } finally {
      setLoading(false);
    }
  }, [horizonWeeks]);

  useEffect(() => {
    loadForecast();
  }, [loadForecast]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!forecast) return null;

  const { summary, weekly_buckets, top_clients, risk_alerts } = forecast;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Flujo de Caja Proyectado</Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Horizonte</InputLabel>
          <Select
            value={horizonWeeks}
            label="Horizonte"
            onChange={(e) => setHorizonWeeks(Number(e.target.value))}
          >
            <MenuItem value={4}>4 semanas</MenuItem>
            <MenuItem value={8}>8 semanas</MenuItem>
            <MenuItem value={12}>12 semanas</MenuItem>
            <MenuItem value={16}>16 semanas</MenuItem>
            <MenuItem value={26}>26 semanas</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KPICard
            title="Total Pendiente"
            value={formatCurrency(summary.total_outstanding)}
            subtitle={`${formatCurrency(summary.total_overdue)} vencido`}
            icon={<AttachMoney />}
            color="#1976d2"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KPICard
            title="Esperado 30 Dias"
            value={formatCurrency(summary.expected_30d)}
            subtitle={`Nueva facturacion: ${formatCurrency(summary.projected_new_billing_30d)}`}
            icon={<AccountBalanceWallet />}
            color="#4caf50"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KPICard
            title="Dias Promedio Cobro"
            value={`${summary.avg_collection_days} dias`}
            subtitle="Promedio ponderado"
            icon={<Schedule />}
            color="#ff9800"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KPICard
            title="Clientes en Riesgo"
            value={String(summary.clients_at_risk_count)}
            subtitle="Alto + Critico con balance"
            icon={<PeopleOutline />}
            color="#f44336"
          />
        </Grid>
      </Grid>

      {/* Timeline Chart + Risk Alerts */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Proyeccion Semanal de Ingresos
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={weekly_buckets}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
                <YAxis tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(value: number) => formatCurrency(value)}
                  labelFormatter={(label: string) => `Semana: ${label}`}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="best_case"
                  name="Mejor Caso"
                  stroke="#4caf50"
                  fill="#4caf50"
                  fillOpacity={0.1}
                  strokeDasharray="5 5"
                />
                <Area
                  type="monotone"
                  dataKey="expected_case"
                  name="Caso Esperado"
                  stroke="#1976d2"
                  fill="#1976d2"
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="worst_case"
                  name="Peor Caso"
                  stroke="#f44336"
                  fill="#f44336"
                  fillOpacity={0.1}
                  strokeDasharray="5 5"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper sx={{ p: 2, height: '100%', maxHeight: 430, overflow: 'auto' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="warning" /> Alertas de Riesgo
            </Typography>
            {risk_alerts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No hay alertas de riesgo activas
              </Typography>
            ) : (
              risk_alerts.map((alert, idx) => (
                <Alert
                  key={idx}
                  severity={alert.severity === 'high' ? 'error' : alert.severity === 'medium' ? 'warning' : 'info'}
                  sx={{ mb: 1 }}
                  icon={<ErrorOutline />}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {alert.client_name || alert.client_id}
                  </Typography>
                  <Typography variant="caption">{alert.message}</Typography>
                </Alert>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Breakdown Bar Chart */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Composicion Semanal (Caso Esperado)
        </Typography>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={weekly_buckets}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
            <YAxis tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(value: number) => formatCurrency(value)} />
            <Legend />
            <Bar dataKey="from_outstanding_invoices" name="Facturas Pendientes" stackId="a" fill="#1976d2" />
            <Bar dataKey="from_projected_billing" name="Facturacion Proyectada" stackId="a" fill="#4caf50" />
            <Bar dataKey="from_overdue_recovery" name="Recuperacion Vencidos" stackId="a" fill="#ff9800" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Client Breakdown Table */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Desglose por Cliente
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell align="right">Pendiente</TableCell>
                <TableCell align="right">Esperado 30d</TableCell>
                <TableCell align="center">Confiabilidad</TableCell>
                <TableCell align="center">Riesgo</TableCell>
                <TableCell align="right">Dias Atraso Prom.</TableCell>
                <TableCell align="center">Tendencia</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {top_clients.map((client: ClientForecastRow) => (
                <TableRow key={client.client_id} hover>
                  <TableCell>
                    <MuiTooltip title={client.client_id}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                        {client.client_name}
                      </Typography>
                    </MuiTooltip>
                  </TableCell>
                  <TableCell align="right">{formatCurrency(client.outstanding_amount)}</TableCell>
                  <TableCell align="right">{formatCurrency(client.expected_collection_30d)}</TableCell>
                  <TableCell align="center">
                    {client.reliability_score !== null ? `${client.reliability_score.toFixed(0)}%` : '—'}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={getRiskLabel(client.risk_level)}
                      size="small"
                      sx={{
                        backgroundColor: getRiskColor(client.risk_level),
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {client.avg_days_overdue !== null ? `${client.avg_days_overdue.toFixed(0)}d` : '—'}
                  </TableCell>
                  <TableCell align="center">
                    {client.improving_trend && (
                      <MuiTooltip title="Mejorando">
                        <TrendingUp sx={{ color: '#4caf50' }} />
                      </MuiTooltip>
                    )}
                    {client.worsening_trend && (
                      <MuiTooltip title="Empeorando">
                        <TrendingDown sx={{ color: '#f44336' }} />
                      </MuiTooltip>
                    )}
                    {!client.improving_trend && !client.worsening_trend && '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default CashFlow;
